import { createApp } from 'vue';
import VueCookies from 'vue3-cookies';
import './assets/css/tokens.css';
import './assets/css/admin-ui.css';
import './style.css';
import LightBootstrap from "./light-bootstrap-main.js";
import App from './App.vue';
// Pas 1.1: @aws-amplify/ui-vue is NOT registered globally anymore —
// only Login renders <Authenticator> (it imports it directly), so
// the UI kit + its CSS ship in the lazy login chunk, not on landing.
import { Amplify } from 'aws-amplify';
import { I18n } from 'aws-amplify/utils';
import { pinia } from "@stores/store.js";
import { useDashboardStore } from "@stores/dashboardStore";
import { getCredentialsProvider, CustomCredentialsProvider } from './services/CustomCredentialsProvider';
import { markAuthReady } from '@utils/authReady';
import { Capacitor } from '@capacitor/core';
// Determine if we're on a native platform
const isNative = __IS_NATIVE__;

const webOrigin = window.location.origin;

// App schemes for different environmentsin main
const schemes = {
  development: {
    web: webOrigin,
    native: 'com.publispace.transcriptor.dev://'
  },
  production: {
    web: webOrigin,
    native: 'com.publispace.transcriptor://'
  },
  productionradio: {
    web: webOrigin,
    native: 'com.publispace.transcriptor://'
  }
};

import devConfig from './amplifyconfiguration.dev.json';
import prodConfig from './amplifyconfiguration.prod.json';
import prodRadioConfig from './amplifyconfiguration.prod.radio.json';

import { initializeSocialLogins } from './services/auth.service';
const env = import.meta.env.MODE; // 'development' or 'production'
const scheme = schemes[env].web;
const signInPath = '/signin/';
const signOutPath = '/signout/';
const amplifyConfig = env === 'development' ? devConfig : env === 'productionradio' ? prodRadioConfig : prodConfig;
amplifyConfig.oauth.redirectSignIn = `${scheme}${signInPath}`;
amplifyConfig.oauth.redirectSignOut = `${scheme}${signOutPath}`;

// Authenticator vocabularies (locales/amplify_locale.js) load with the
// Login chunk — the only place the Authenticator renders (Pas 1.1).
import { useSessionStore } from '@stores/session';
// Pas 1.1 repair: pdfjs is NOT pre-warmed here anymore (it downloaded
// ~107 KB gzip on every landing). Its only consumer, documentExtract.ts,
// lazy-imports it and sets the same-origin workerSrc at first PDF use.

import { i18n, setupLocale } from './i18nlocal.js';
import { useThemeStore } from '@stores/themeStore';

// Create the app and initialize Pinia first
const app = createApp(App);
app.use(pinia);

// Global client error pipeline (Vue + window + unhandled rejections) —
// records into the log store, beacons to VITE_ERROR_REPORT_URL when set,
// forwards to Sentry when present. Must come before any mount.
import('./utils/errorTracking').then(({ initErrorTracking }) => initErrorTracking(app)).catch((err) => { void err; });

// The persisted dashboardStore can carry a stale isProductionRadio flag from
// older builds (it used to default to true everywhere). The mode is decided by
// the build, so re-sync it from the environment before anything reads it.
useDashboardStore().isProductionRadio = import.meta.env.MODE === 'productionradio';

// Initialize theme after creating Pinia store
const themeStore = useThemeStore();
themeStore.initializeTheme();

// Now we can use the session store
let locale = useSessionStore().locale || 'ro';
// ro applies synchronously (bundled); a persisted non-ro locale loads
// its chunk in the background and switches when ready (Pas 1.1).
setupLocale(locale).catch(() => setupLocale('ro'));
I18n.setLanguage(locale);

// Continue with the rest of the app setup
app.use(i18n);
app.use(LightBootstrap);
app.use(VueCookies);
import Router from './router/index.js';
app.use(Router);

// Pas 1.1: mount immediately so the public landing paints without
// waiting on the /api/config fetch + Amplify setup. Credentials init
// continues in the background; auth-dependent surfaces (Login,
// SignInCallback, protected routes) await `authReady` instead.
app.mount('#app');

// Move the CustomCredentialsProvider initialization here, after Pinia is available
CustomCredentialsProvider.initialize().then((credentials) => {
  // credentials contain AWS session keys — never log them.
  if (isNative) {
    const customCredentialsProvider = getCredentialsProvider();
    Amplify.configure(amplifyConfig, {
      Auth: {
        credentialsProvider: customCredentialsProvider
      },
    });
  }
  else {
    amplifyConfig.aws_project_region = credentials.identityPoolId.split(':')[0];
    amplifyConfig.aws_cognito_identity_pool_id = credentials.identityPoolId,
    amplifyConfig.aws_cognito_region = credentials.identityPoolId.split(':')[0];
    amplifyConfig.aws_user_pools_id = credentials.userPoolId;
    amplifyConfig.aws_user_pools_web_client_id = credentials.userPoolClientId;
    amplifyConfig.Auth.userPoolId = credentials.userPoolId;
    amplifyConfig.Auth.userPoolWebClientId = credentials.userPoolClientId;
    Amplify.configure(amplifyConfig);
    //console.log('Running on a web platform', JSON.stringify(amplifyConfig));
  }

  // Initialize social logins after Amplify configuration, then open
  // the gate for auth-dependent surfaces waiting on `authReady`.
  initializeSocialLogins();
  markAuthReady(true);
}).catch((error) => {
  console.error('Error initializing CustomCredentialsProvider:', error);
  // The app is already mounted; release auth waiters so Login can
  // surface its own error state instead of hanging (old behavior:
  // mount even on error).
  initializeSocialLogins();
  markAuthReady(false);
});
